import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Layers, 
  Lightbulb, 
  Diamond, 
  Shield,
  Share2,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navItems = [
  { path: '/dashboard', label: 'My Record', icon: LayoutDashboard, description: 'Your leadership map' },
  { path: '/sources', label: 'Sources', icon: Layers, description: 'Uploaded documents' },
  { path: '/insights', label: 'Insights', icon: Lightbulb, description: 'Strata Record Synthesis' },
  { path: '/proof', label: 'Proof', icon: Diamond, description: 'Role fit & documents' },
  { path: '/share', label: 'Share', icon: Share2, description: 'Credentials & sharing' },
  { path: '/record', label: 'Record', icon: Shield, description: 'Account & purchases' },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-slate-mid/50 bg-obsidian px-6 pb-4">
          {/* Logo */}
          <div className="flex h-20 shrink-0 items-center">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/strata-lotus-mark.svg" 
                alt="Strata Record" 
                className="w-10 h-10"
              />
              <span className="font-display text-2xl text-white">Strata Record</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      className={`group flex gap-x-3 rounded-xl p-3 text-sm font-medium leading-6 transition-all duration-300 ${
                        isActive
                          ? 'bg-slate text-white border-l-4 border-amber-600'
                          : 'text-slate-400 hover:text-white hover:bg-slate/50'
                      }`}
                    >
                      <item.icon className={`h-6 w-6 shrink-0 ${isActive ? 'text-amber-500' : ''}`} />
                      <div>
                        <span className="block">{item.label}</span>
                        <span className="text-xs text-slate-500">{item.description}</span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User section */}
          {user && (
            <div className="mt-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-3 w-full p-3 rounded-xl hover:bg-slate/50 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="h-10 w-10 border-2 border-slate-mid">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-amber-600 text-white">
                        {user.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium text-white truncate">{user.name}</p>
                      <p className="text-xs text-slate-400 truncate">{user.email}</p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-slate border-slate-mid">
                  <DropdownMenuItem asChild>
                    <Link to="/record" className="cursor-pointer">
                      <Shield className="mr-2 h-4 w-4" />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-mid" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400 cursor-pointer" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile header */}
      <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-obsidian/95 backdrop-blur px-4 py-4 shadow-sm sm:px-6 lg:hidden border-b border-slate-mid/50">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-slate-400 lg:hidden"
          onClick={() => setMobileMenuOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex-1 flex items-center gap-3">
          <img 
            src="/strata-lotus-mark.svg" 
            alt="Strata Record" 
            className="w-8 h-8"
          />
          <span className="font-display text-xl text-white">Strata Record</span>
        </div>
        {user && (
          <Avatar className="h-8 w-8 border border-slate-mid">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-amber-600 text-white text-sm">
              {user.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
        )}
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-full max-w-xs border-r border-slate-700" style={{ backgroundColor: '#0D0F14' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-mid/50">
              <div className="flex items-center gap-3">
                <img 
                  src="/strata-lotus-mark.svg" 
                  alt="Strata Record" 
                  className="w-8 h-8"
                />
                <span className="font-display text-xl text-white">Strata Record</span>
              </div>
              <button
                type="button"
                className="-m-2.5 p-2.5 text-slate-400"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="px-4 py-6">
              <ul className="space-y-2">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`group flex gap-x-3 rounded-xl p-3 text-sm font-medium leading-6 transition-all duration-300 ${
                          isActive
                            ? 'bg-slate text-white border-l-4 border-amber-600'
                            : 'text-slate-400 hover:text-white hover:bg-slate/50'
                        }`}
                      >
                        <item.icon className={`h-6 w-6 shrink-0 ${isActive ? 'text-amber-500' : ''}`} />
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              {user && (
                <div className="mt-8 pt-6 border-t border-slate-mid/50">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full p-3 rounded-xl text-red-400 hover:bg-slate/50 transition-colors"
                  >
                    <LogOut className="h-6 w-6" />
                    Sign Out
                  </button>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="lg:pl-72">
        <div className="px-4 py-8 sm:px-6 lg:px-12 lg:py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
